// TODO: Refactor. This component replaced for Image and VideoPreview component and any place using img,video
import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cn, isVideoThumbnail } from '@anytag/shared-utils';
import { useContainerQuery } from '@anytag/shared-hooks';
import { IconNames, emptyImage, emptyVideoImage } from '@anytag/shared-assets';
import { Icon } from '../Icon';

export interface MediaPreviewProps {
  alt?: string;
  counter?: { index: number; total: number };
  icon?: IconNames;
  isReel?: boolean;
  caption?: string | null;
  className?: string;
  showIcon?: boolean;
  playVideo?: boolean;
  src?: string | null;
  onClick?: () => void;
  defaultImage?: string;
  objectFit?: 'cover' | 'contain';
  captionProps?: {
    height?: number;
    margin?: number;
    padding?: number;
    lineHeight?: number;
  };
  postReportCaption?: ReactNode;
  style?: React.CSSProperties;
}

export const MediaPreview = ({
  alt,
  src,
  icon,
  style, // we can use to set dynamic css directly, but maybe better to create variance instead
  isReel,
  caption,
  onClick,
  showIcon,
  className,
  playVideo,
  defaultImage,
  objectFit = 'contain',
  counter,
  captionProps,
  postReportCaption: PostReportCaption
}: MediaPreviewProps) => {
  const [error, setError] = useState<boolean>(false);
  const { t } = useTranslation();
  const isVideo = isVideoThumbnail(src);
  const { containerRef, height } = useContainerQuery();
  const customIcon = icon || ((isReel ? 'clapper' : isVideo ? 'play' : '') satisfies IconNames | '');
  const videoProps = playVideo ? { loop: true, muted: true, autoPlay: true, playsInline: true, controls: true } : {};
  const hasPreview = src && !error;

  return (
    <div
      onClick={onClick}
      className={cn(
        styles.wrapper,
        {
          'pointer-events-none': !onClick
        },
        className
      )}
      ref={containerRef}
      style={style}
    >
      {hasPreview ? (
        <>
          {showIcon && !!customIcon ? (
            <Icon size="16px" variant="white" icon={customIcon} className={styles.icon} />
          ) : null}

          {isVideo ? (
            <video preload="metadata" className={styles.mediaSize} {...videoProps}>
              <source src={`${src}#t=1`} type="video/mp4" onError={() => setError(true)} />
            </video>
          ) : (
            <img
              alt={alt}
              src={src}
              loading="lazy"
              onError={() => setError(true)}
              className={cn('media-preview-img', styles.mediaSize)}
              style={{ objectFit }}
            />
          )}
        </>
      ) : isReel ? (
        <div className={cn(styles.igReelNoPreview, '@container')}>
          <div className={styles.igReelNoPreviewText}>
            <Icon icon="eye-off" variant="white" width={24} />
            <p className="no-preview-text">
              {t('Reels video is not available due to the media contains copyrighted material')}
            </p>
          </div>
        </div>
      ) : // don't have preview, either image placeholder or captionUI
      PostReportCaption ? (
        PostReportCaption
      ) : caption ? (
        <div
          className={styles.caption}
          style={{
            height: `${height - ((captionProps?.margin || DEFAULT_CAPTION_PROPS.margin) * 2 + 2 * (captionProps?.padding || DEFAULT_CAPTION_PROPS.padding))}px`,
            margin: `${captionProps?.margin || DEFAULT_CAPTION_PROPS.margin}px`,
            padding: `${captionProps?.padding || DEFAULT_CAPTION_PROPS.padding}px`,
            fontSize: `${captionProps?.lineHeight || DEFAULT_CAPTION_PROPS.lineHeight - 3}px`
          }}
        >
          <p
            title={caption}
            className="text-caption overflow-hidden break-all whitespace-normal"
            style={{
              lineHeight: `${captionProps?.lineHeight || DEFAULT_CAPTION_PROPS.lineHeight}px`,
              WebkitLineClamp: Math.floor(
                (height - (captionProps?.margin || DEFAULT_CAPTION_PROPS.margin) * 2) /
                  (captionProps?.lineHeight || DEFAULT_CAPTION_PROPS.lineHeight)
              ),
              WebkitBoxOrient: 'vertical'
            }}
          >
            {caption}
          </p>
        </div>
      ) : (
        <img
          alt={alt}
          src={defaultImage || (isVideo ? emptyVideoImage : emptyImage)}
          className={cn(styles.mediaSize, 'bg-black-30 object-contain')}
        />
      )}

      {counter ? (
        <div className={styles.counterWrapper}>
          <Icon icon="img" size={8} variant="white" />
          <p className="text-[8px] leading-[100%] font-semibold text-white">
            {counter.index}/{counter.total}
          </p>
        </div>
      ) : null}
    </div>
  );
};

const DEFAULT_CAPTION_PROPS = {
  height: 0,
  lineHeight: 10,
  margin: 8,
  padding: 3
};

const styles = {
  mediaSize: cn('h-[inherit] max-h-[inherit] w-[inherit] max-w-[inherit]'),
  wrapper: cn(
    'relative box-border h-full w-full flex-none cursor-pointer overflow-hidden rounded-[inherit] bg-black opacity-100',
    'transition-opacity duration-300 hover:opacity-90'
  ),
  icon: cn('absolute top-1 right-1 z-1'),
  igReelNoPreview: cn('m-auto flex h-full items-center justify-center bg-black-90'),
  igReelNoPreviewText: cn(
    'flex flex-col items-center gap-4 p-6 text-center text-base font-normal text-white',
    '@max-[200px]:[&_.no-preview-text]:hidden'
  ),
  caption: cn(
    'relative rounded-[5px] bg-white p-3 text-base font-normal',
    /* triangle */
    'after:absolute after:bottom-[-8px] after:left-[6px] after:border-4 after:border-solid after:border-[#fff_transparent_transparent_transparent] after:content-[""]'
  ),
  counterWrapper: cn(
    'absolute right-0.5 bottom-0.5 box-border flex h-3.5 min-w-8 items-center justify-center gap-0.75 rounded-[13px] bg-black-70 px-0.75 py-1'
  )
};
